import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  CardMedia,
  Typography,
  List,
  ListItem,
  CircularProgress,
  Grid,
  TextField,
  Box,
  Chip,
  OutlinedInput,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import PlaceIcon from "@mui/icons-material/Place";
import SortIcon from "@mui/icons-material/Sort";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import GroupIcon from "@mui/icons-material/Group";
import ChatIcon from "@mui/icons-material/Chat";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { isLoggedIn } from "../session";
import { db, auth } from "../firebase";
import { collection, getDocs, where, query } from "firebase/firestore";
import logoSvg from "../imgs/logo_border.png";
import BottomToolbar from "./BottomNav";
import { MembershipBadge, getTierInfo } from "./membershipTiers";
import { CertificationChip } from "./certifications";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const Members = () => {
  const [freelancers, setFreelancers] = useState([]);
  const [nameSearch, setNameSearch] = useState("");
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [locationFilter, setLocationFilter] = useState("");
  const [sortBy, setSortBy] = useState("relevance");
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allSkills, setAllSkills] = useState([]);
  const [allLocations, setAllLocations] = useState([]);

  const navigate = useNavigate();

  const filterFreelancers = useCallback(() => {
    const nameSearchLower = nameSearch.toLowerCase();
    const filteredFreelancers = freelancers.filter(
      (freelancer) =>
        freelancer.displayName.toLowerCase().includes(nameSearchLower) &&
        (selectedSkills.length === 0 ||
         selectedSkills.every(skill => freelancer.skills.includes(skill))) &&
        (!locationFilter || freelancer.point === locationFilter)
    );

    const sorted = [...filteredFreelancers].sort((a, b) => {
      if (sortBy === "name") return a.displayName.localeCompare(b.displayName);
      if (sortBy === "skills") return (b.skills?.length || 0) - (a.skills?.length || 0);
      return 0;
    });

    // Los planes pagos aparecen primero (beneficio de la suscripción), preservando
    // el orden elegido por el usuario dentro de cada nivel.
    const withMembershipBoost = [...sorted].sort(
      (a, b) => getTierInfo(b.subscription).rank - getTierInfo(a.subscription).rank
    );

    setFilteredResults(withMembershipBoost);
  }, [nameSearch, selectedSkills, locationFilter, sortBy, freelancers]);

  const handleClearFilters = () => {
    setNameSearch("");
    setSelectedSkills([]);
    setLocationFilter("");
    setSortBy("relevance");
  };

  const hasActiveFilters = nameSearch || selectedSkills.length > 0 || locationFilter || sortBy !== "relevance";

  useEffect(() => {
    filterFreelancers();
  }, [filterFreelancers]);

  useEffect(() => {
    const fetchFreelancers = async () => {
      try {
        const isAuthenticated = await isLoggedIn();
        if (isAuthenticated) {
          setLoading(true);
          const freelancersCollection = collection(db, "users");
          const q = query(
            freelancersCollection,
            where("userType", "==", "freelancer")
          );
          const freelancersSnapshot = await getDocs(q);
          const freelancersData = freelancersSnapshot.docs.map((doc) => ({
            key: doc.id,
            ...doc.data(),
          }));
          setFreelancers(freelancersData);
          setFilteredResults(freelancersData);

          // Extract all unique skills
          const skills = [...new Set(freelancersData.flatMap(f => f.skills))];
          setAllSkills(skills);

          // Extract all unique locations
          const locations = [...new Set(freelancersData.map(f => f.point).filter(Boolean))];
          setAllLocations(locations);

          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching freelancers:", error);
        setLoading(false);
      }
    };
    fetchFreelancers();
  }, []);

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/login");
    }
  }, [navigate]);

  const handleNameSearchChange = (event) => {
    setNameSearch(event.target.value);
  };

  const handleSkillChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedSkills(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  return (
    <Container>
      <AppBar position="fixed">
        <Toolbar sx={{ flexDirection: "column", alignItems: "center" }}>
          <CardMedia
            component="img"
            image={logoSvg}
            title="Skach"
            sx={{ width: 160, marginBottom: 2 }}
          />
          <Typography variant="h6" sx={{ color: "text.secondary", fontSize: 12, fontWeight: "bold" }}>
            ¡Crea un proyecto o busca uno!
          </Typography>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 15, mb: 10 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
            <TextField
              fullWidth
              label="Buscar por nombre"
              variant="outlined"
              value={nameSearch}
              onChange={handleNameSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel id="skill-select-label">Habilidades</InputLabel>
              <Select
                labelId="skill-select-label"
                multiple
                value={selectedSkills}
                onChange={handleSkillChange}
                input={<OutlinedInput label="Habilidades" />}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterListIcon color="action" />
                  </InputAdornment>
                }
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {allSkills.map((skill) => (
                  <MenuItem
                    key={skill}
                    value={skill}
                  >
                    {skill}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="location-select-label">Ubicación</InputLabel>
              <Select
                labelId="location-select-label"
                value={locationFilter}
                label="Ubicación"
                onChange={(e) => setLocationFilter(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <PlaceIcon color="action" />
                  </InputAdornment>
                }
              >
                <MenuItem value="">Todas</MenuItem>
                {allLocations.map((location) => (
                  <MenuItem key={location} value={location}>
                    {location}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="sort-select-label">Ordenar por</InputLabel>
              <Select
                labelId="sort-select-label"
                value={sortBy}
                label="Ordenar por"
                onChange={(e) => setSortBy(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SortIcon color="action" />
                  </InputAdornment>
                }
              >
                <MenuItem value="relevance">Relevancia</MenuItem>
                <MenuItem value="name">Nombre (A-Z)</MenuItem>
                <MenuItem value="skills">Más habilidades</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {hasActiveFilters && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Chip
                icon={<RestartAltIcon />}
                label="Limpiar filtros"
                onClick={handleClearFilters}
                variant="outlined"
              />
            </Box>
          )}
        </Paper>
        {!loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <GroupIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {filteredResults.length} freelancer{filteredResults.length !== 1 ? "s" : ""} encontrado{filteredResults.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress />
          </Box>
        ) : filteredResults.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, color: "text.secondary" }}>
            <PersonSearchIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body1">No se encontraron freelancers</Typography>
            <Typography variant="body2">Probá ajustando los filtros de búsqueda</Typography>
          </Box>
        ) : (
          <List>
            {filteredResults.map((freelancer) => (
              <ListItem
                key={freelancer.key}
                onClick={() => navigate(`/freelancer/${freelancer.key}`)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  mb: 2,
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "box-shadow 0.2s, border-color 0.2s",
                  "&:hover": {
                    boxShadow: 3,
                    borderColor: "primary.main",
                  },
                }}
              >
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <CardMedia
                      component="img"
                      image={freelancer.profileImageUrl}
                      title="Perfil"
                      sx={{ width: 48, height: 48, borderRadius: "50%", border: "2.5px solid", borderColor: "secondary.main" }}
                    />
                  </Grid>
                  <Grid item xs>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body1" color="primary" fontWeight={600}>
                        {freelancer.displayName}
                      </Typography>
                      <MembershipBadge subscription={freelancer.subscription} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                      <PlaceIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {freelancer.point}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                      {freelancer.skills.map((skill) => (
                        <Chip key={skill} label={skill} size="small" variant="outlined" />
                      ))}
                    </Box>
                    {Object.entries(freelancer.skillAssessments || {}).filter(([, a]) => a.displayed).length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                        {Object.entries(freelancer.skillAssessments)
                          .filter(([, a]) => a.displayed)
                          .map(([skill, a]) => (
                            <CertificationChip key={skill} skill={skill} score={a.score} />
                          ))}
                      </Box>
                    )}
                    {freelancer.key !== auth.currentUser?.uid && (
                      <Box sx={{ display: "flex", gap: 1, mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<ChatIcon />}
                          onClick={() => navigate(`/messages/${freelancer.key}`)}
                        >
                          Enviar mensaje
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </ListItem>
            ))}
          </List>
        )}
      </Container>
      <BottomToolbar />
    </Container>
  );
};

export default Members;